import { IconButton, Stack, Typography } from '@mui/material';
import {
  DefaultSchoolYear,
  FilterKeys,
  FilterSection,
  GetGroupsQueryFilters,
  GroupArrangeBy,
  GROUPS_ARRANGE_BY_FILTER_KEYS,
  GROUPS_QUERY_FILTER_KEYS,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  ArrangeByDropdown,
  DateRangeDropdown,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  PropertyTypeTagSelect,
  renderPropertyGroupTags,
  StudentStaffPropertyTypeExpandedSelect,
  SubjectExpandedSelect,
  SubjectTagSelect,
  UserExpandedSelect,
  UserTagSelect,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { CloseSmallIcon } from '@schooly/style';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { GroupsFiltersSidebar } from './GroupsFiltersSidebar';
import { useGroupsFilters } from './useGroupsFilters';

export type GroupFilters = {
  openArrangeBy: () => void;
};
type GroupsFiltersProps = {
  arrangeBy: GroupArrangeBy | null;
  onSetArrangeBy: (v: GroupArrangeBy | null) => void;
  schoolId: string;
  filters: GetGroupsQueryFilters;
  defaultFilters: GetGroupsQueryFilters;
  onSetFilters: (v: GetGroupsQueryFilters) => void;
  notActualInitialDate?: boolean;
  defaultSchoolYear?: DefaultSchoolYear;
  defaultUserFilters: GetGroupsQueryFilters;
  defaultUserArrangeBy: GroupArrangeBy | null;
};

const GroupsFiltersComponent = (
  {
    arrangeBy,
    defaultFilters,
    filters,
    schoolId,
    onSetFilters,
    onSetArrangeBy,
    defaultSchoolYear,
    defaultUserFilters,
    defaultUserArrangeBy,
    notActualInitialDate,
  }: GroupsFiltersProps,
  ref: React.ForwardedRef<GroupFilters>,
) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();

  const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);
  const {
    labels,
    arrangeByDropdownOptions,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersSubject,
    filtersStaff,
    filtersStudentStatus,
    filtersStaffStatus,
    filtersStudentHouse,
    filtersStaffHouse,
    filtersOnlyTutorGroups,
    onSetDate,
    onSetAgeGroup,
    onClearAgeGroup,
    onSetSubjectId,
    onClearSubject,
    onSetStaffId,
    onClearStaff,
    onSetStudentStaffStatusIds,
    onClearStatuses,
    onSetStudentStaffHouseIds,
    onClearHouses,
    onClearOnlyTutorGroups,
    getSchoolLevelById,
    getAgeGroupById,
    getAgeGroupsByLevelId,
  } = useGroupsFilters({ schoolId, filters, onSetFilters });

  useImperativeHandle(
    ref,
    () => ({
      openArrangeBy: () => {
        setTimeout(() => arrangeByDropdown.current?.open(), 300);
      },
    }),
    [],
  );

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        data-test-id={'filter-personal'}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Group}
        filters={{ ...filters, arrange_by: arrangeBy ?? undefined }}
        defaultSchoolYear={defaultSchoolYear}
        onSetFilters={(v) => {
          const appliedFilters = pickOnlyParamsFromFilterKeys(GROUPS_QUERY_FILTER_KEYS, v);

          onSetFilters({
            ...appliedFilters,
            [FilterKeys.Date]: appliedFilters.date || defaultFilters.date,
          });

          if (!v.arrange_by) {
            onSetArrangeBy(null);
            return;
          }
          for (const key of GROUPS_ARRANGE_BY_FILTER_KEYS) {
            key === v.arrange_by && onSetArrangeBy(key);
          }
        }}
      />
      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultSchoolYear}
          notActualInitialDate={notActualInitialDate}
        />
      )}

      {!!(filtersStudentStatus?.length || filtersStaffStatus?.length) && (
        <FilterDropdown
          onClear={onClearStatuses}
          data-test-id={'filter-status'}
          label={labels.status}
          tags={(open) =>
            (filtersStudentStatus || [])
              .map((id) => (
                <PropertyTypeTagSelect
                  userRole={SchoolUserRole.Student}
                  sx={{ maxWidth: 200 }}
                  schoolId={schoolId}
                  key={id}
                  id={id}
                  onClick={open}
                />
              ))
              .concat(
                (filtersStaffStatus || []).map((id) => (
                  <PropertyTypeTagSelect
                    userRole={SchoolUserRole.Staff}
                    sx={{ maxWidth: 200 }}
                    schoolId={schoolId}
                    key={id}
                    id={id}
                    onClick={open}
                  />
                )),
              )
          }
        >
          {(onClose) => (
            <StudentStaffPropertyTypeExpandedSelect
              selectedStudentIds={filtersStudentStatus || []}
              selectedStaffIds={filtersStaffStatus || []}
              schoolId={schoolId}
              onClose={onClose}
              onApply={(studentIds, staffIds) => {
                onSetStudentStaffStatusIds({ studentIds, staffIds });
              }}
              onClear={onClearStatuses}
              propertyType={SchoolPropertyType.Status}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersAgeGroup?.length && (
        <FilterDropdown
          onClear={onClearAgeGroup}
          data-test-id={'filter-age-group'}
          label={labels[FilterKeys.AgeGroup]}
          tags={(open) =>
            renderPropertyGroupTags({
              selectedItems: selectedItemsForAgeGroups,
              onClick: open,
              getProperty: (i) =>
                i.isGroup
                  ? getSchoolLevelById(i.id)
                  : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
              getTooltip: (i) =>
                i.isGroup
                  ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                      <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                    ))
                  : null,
              tagProps: {
                userRole: SchoolUserRole.Student,
                sx: { maxWidth: 200 },
              },
            })
          }
        >
          {(onClose) => (
            <AgeGroupExpandedSelect
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersAgeGroup}
              onApply={onSetAgeGroup}
              onClose={onClose}
              onClear={onClearAgeGroup}
            />
          )}
        </FilterDropdown>
      )}
      {!!(filtersStudentHouse?.length || filtersStaffHouse?.length) && (
        <FilterDropdown
          onClear={onClearHouses}
          data-test-id={'filter-house'}
          label={labels.house}
          tags={(open) =>
            (filtersStudentHouse || [])
              .map((id) => (
                <PropertyTypeTagSelect
                  userRole={SchoolUserRole.Student}
                  sx={{ maxWidth: 200 }}
                  schoolId={schoolId}
                  key={id}
                  id={id}
                  onClick={open}
                />
              ))
              .concat(
                (filtersStaffHouse || []).map((id) => (
                  <PropertyTypeTagSelect
                    userRole={SchoolUserRole.Staff}
                    sx={{ maxWidth: 200 }}
                    schoolId={schoolId}
                    key={id}
                    id={id}
                    onClick={open}
                  />
                )),
              )
          }
        >
          {(onClose) => (
            <StudentStaffPropertyTypeExpandedSelect
              selectedStudentIds={filtersStudentHouse || []}
              selectedStaffIds={filtersStaffHouse || []}
              schoolId={schoolId}
              onClose={onClose}
              onApply={(studentIds, staffIds) => {
                onSetStudentStaffHouseIds({ studentIds, staffIds });
              }}
              onClear={onClearHouses}
              propertyType={SchoolPropertyType.House}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersStaff?.length && (
        <FilterDropdown
          onClear={onClearStaff}
          data-test-id={'filter-staff'}
          label={labels[FilterKeys.Staff]}
          tags={(open) =>
            filtersStaff.map((id) => (
              <UserTagSelect
                userType="staff"
                currentUser={currentStaff}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <UserExpandedSelect
              type="staff"
              schoolId={schoolId}
              currentUser={currentStaff}
              selectedIds={filtersStaff}
              onApply={onSetStaffId}
              onClose={onClose}
              onClear={onClearStaff}
              filters={filters}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersSubject?.length && (
        <FilterDropdown
          onClear={onClearSubject}
          data-test-id={'filter-subject'}
          label={labels[FilterKeys.Subject]}
          tags={(open) =>
            filtersSubject.map((id) => (
              <SubjectTagSelect
                schoolId={schoolId}
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <SubjectExpandedSelect
              schoolId={schoolId}
              selectedIds={filtersSubject}
              onApply={onSetSubjectId}
              onClose={onClose}
              onClear={onClearSubject}
            />
          )}
        </FilterDropdown>
      )}
      {filtersOnlyTutorGroups && (
        <Stack direction="row" gap={1}>
          <Typography variant="h3">
            <FormattedMessage id="groups-OnlyTutorGroups" />
          </Typography>
          <IconButton
            sx={{ fontSize: (theme) => theme.spacing(2) }}
            onClick={onClearOnlyTutorGroups}
          >
            <CloseSmallIcon />
          </IconButton>
        </Stack>
      )}
      {arrangeBy && (
        <ArrangeByDropdown
          ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
          data-test-id={'filter-arrange-by'}
          onSelectOption={onSetArrangeBy}
          selectedOption={arrangeBy}
          options={arrangeByDropdownOptions}
          onClear={() => onSetArrangeBy(null)}
          onRemove={() => onSetArrangeBy(null)}
        />
      )}
      <GroupsFiltersSidebar
        ref={sidebar}
        defaultSchoolYear={defaultSchoolYear}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={(v) => {
          onSetFilters(v.filters);
          onSetArrangeBy(v.arrangeBy);
        }}
        arrangeBy={arrangeBy}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};

export const GroupsFilters = forwardRef(GroupsFiltersComponent);
